import {
  type CommunicationTemplatesSettings,
  type Opportunity,
  type ParentTaskType,
  type SubTaskType,
  type TaskContext,
  type WorkspaceTemplates,
  type hfb,
} from "@repo/api";
import { type IconName } from "@repo/skapa";
import { DEFAULT_LOCALE, getLanguageAndLocaleFromUrl } from "@repo/translation";
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import useSWR from "swr";

import {
  getCustomerModeFromStorage,
  setCustomerModeToStorage,
} from "../components/CustomerModeToggle";
import { type Environment, environment } from "./environment";

interface Props {
  children: ReactNode;
}

interface ServiceAreaConfig {
  name_key: string;
  services?: { name_key: string }[];
}

export interface StoreConfig {
  unit_id_key: string;
  enable: boolean;
  name?: string;
  zip_code?: string;
  service_configs?: {
    service_name_key: string;
    zip_code?: string;
    pre_call_task_config?: {
      enable?: boolean;
      pre_call_due_hours?: string;
      pre_call_assignee?: string;
      create_pre_call_for_follow_up_appointments?: boolean;
    };
  }[];
}

export interface HfbConfig {
  service_areas?: ServiceAreaConfig[];
  boka_link?: string;
}

interface TranslationConfig {
  language: string;
  message: string;
}

export interface ConsentRequirements {
  require_consent: boolean;
  require_followup_consent: boolean;
  require_privacy_policy: boolean;
  default_followup_consent?: "YES" | "NO";
  disable_manual_consent?: boolean;
  send_consent_approval?: boolean;
  promotional_consent?: boolean;
  enable_do_not_contact?: boolean;
  disable_project_creation?: boolean;
}

export interface HfbConfiguration {
  hfb_list?: [
    {
      hfb_code_key?: hfb;
      hfb_title?: string;
      boka_link?: string;
      service_areas?: ServiceAreaConfig[];
    },
  ];
}

export interface TaskConfigBase {
  old_name_key?: string;
  description?: string;
  enabled?: boolean;
  icon?: IconName;
  colour?: {
    name: string;
    tokenName: string;
    token: string;
    contrast: "light" | "dark";
  };
  context?: TaskContext[];
  hfbs?: hfb[];
  translationKey?: string;
  translations?: TaskTranslation[];
}

export interface TaskConfig extends TaskConfigBase {
  name_key: ParentTaskType;
  isCompletable: boolean;
  subTypes?: SubTaskConfig[];
}

export interface SubTaskConfig extends TaskConfigBase {
  name_key: SubTaskType;
  hasTitle?: boolean;
  isDateRangeOnlyTask?: boolean;
  isDueDateTask?: boolean;
  timeInMinutes?: number;
  allDay?: boolean;
}

export interface TaskTranslation {
  language_key: string;
  translation: string;
}

export interface ActivityConfig {
  activity_key: string;
  translations: TaskTranslation[];
  colour?: {
    name: string;
    tokenName: string;
    token: string;
    contrast: "light" | "dark";
  };
  hide_activity?: boolean;
}

export interface Stage {
  stage_key: string;
  name: string;
  actions: { action_key: string }[];
}

export interface StageAction {
  action_key: string;
  name: string;
  description?: string;
  icon?: IconName;
}

export interface Settings {
  customerMode: boolean; // from Local storage
  setCustomerMode: (value: boolean) => void; // function to update Local storage
  isLoadingSettings?: boolean;
  market: string;
  language: string;
  timezone: string;
  environment: Environment;
  feature_flags?: {
    playground?: boolean;
    b2b_enr_enabled?: boolean;
    restrict_search_by_coworker?: boolean;
    enable_subheader_fragment?: boolean;
    enable_box_projects?: boolean;
    enable_salesforce_integration?: boolean;
    enable_b2b_home_project_attachment?: boolean;
    enable_customer_communication?: boolean;
    abbreviate_coworker_name?: boolean;
    enable_workspace_templates?: boolean;
    projects_intro?: boolean;
    calendar_intro?: boolean;
  };
  markets?: string[];
  general?: {
    currency: {
      currency_code: string;
      currency_symbol: string;
    };
    localization?: {
      reverse_names: boolean;
      disable_decimals: boolean;
      use_address_format: boolean;
    };
    default_language_code: string;
    supported_languages: string[];
    country_name: string;
    order_source: string;
    reasons?: {
      classification_change: string[];
      project_status_change: Record<
        Opportunity["status"],
        string[] | undefined
      >;
    };
  };
  consent_requirements?: ConsentRequirements;
  health_monitoring?: {
    sales_lead_alert?: {
      enabled: boolean;
      message: string;
      clearing_time: string;
      emergency: boolean;
      translation?: TranslationConfig[];
    };
  };
  system_failure?: {
    failure: boolean;
    message: string;
  };
  boka_config?: {
    enable: boolean;
    enable_all_stores_and_services?: boolean;
    enable_boka_appointment_modal_integration?: boolean;
    enable_boka_follow_up_integration?: boolean;
    stores?: StoreConfig[];
    hfb: Partial<Record<hfb, HfbConfig>>;
    hfb_configuration?: HfbConfiguration;
  };
  nba?: {
    enable: boolean;
  };
  calendar?: {
    drop_in?: boolean;
    follow_up?: boolean;
    show_people_and_planning_blocks?: boolean;
    edit_schedule_entries?: boolean;
    delete_schedule_entries?: boolean;
    upload_schedule?: {
      stores?: {
        enable: boolean;
        unit_id_key: string;
        enable_pandp: boolean;
        enable_schedule_blocks: boolean;
      }[];
    };
    activity_configuration?: ActivityConfig[];
    schedule_visibility_limit?: string;
    hide_absence_blocks?: boolean;
    absence_overrides_availability?: boolean;
    enable_unassigned_open_slots?: boolean;
  };
  quotation?: {
    enable_quotations?: boolean;
    enable_quotations_import?: boolean;
  };
  task_configuration?: { task_types: TaskConfig[] };
  gpc_integration?: {
    stores?: string[]; // unit id
  };
  ai_features_config?: {
    enable_project_summary?: boolean;
    enabled_project_summary_stores?: string[];
    enable_coworker_portfolio_summary?: boolean;
    enabled_portfolio_summary_stores?: string[];
    enable_generated_projects?: boolean;
  };
  customer_communication?: {
    cold_projects?: {
      enable?: boolean;
    };
  };
  project_stages?: {
    enable?: boolean;
    hfb: { hfb_code_key: hfb; enable: boolean }[];
    stages: Stage[];
    actions: StageAction[];
  };
  kic_config?: {
    enabled?: boolean;
    kic_team_id?: string;
  };
  communication_templates?: CommunicationTemplatesSettings;
  waiting_list?: {
    waiting_list_enabled?: boolean;
  };
  workspace_templates?: WorkspaceTemplates;
}

const createSettingsFetcher =
  (country: string, language: string, file: string) => async () => {
    try {
      const url = `${
        import.meta.env.VITE__LOCALE_PREFIX
          ? `${import.meta.env.VITE__LOCALE_PREFIX}/`
          : "/"
      }${country}/${language}/saleslead/settings/${file}`;
      const res = await fetch(url);
      return await res?.json();
    } catch {
      /* If file is not found etc. assume no setting exists */
      return {};
    }
  };

const SettingsContext = createContext<Settings>({} as Settings);

export const SettingsProvider = ({ children }: Props) => {
  const locale = getLanguageAndLocaleFromUrl();
  const { country, language } = locale ?? DEFAULT_LOCALE;

  // use state to trigger re-render when customer mode changes
  const [customerMode, setCustomerMode] = useState(getCustomerModeFromStorage);

  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // sync between fragments/tabs
  useEffect(() => {
    const syncCustomerMode = () => {
      setCustomerMode(getCustomerModeFromStorage());
    };

    // Listen to both:
    // - 'storage' (fires across tabs)
    // - 'customerModeChange' (fires across same-page fragments)
    window.addEventListener("storage", syncCustomerMode);
    window.addEventListener("customerModeChange", syncCustomerMode);

    return () => {
      window.removeEventListener("storage", syncCustomerMode);
      window.removeEventListener("customerModeChange", syncCustomerMode);
    };
  }, []);

  const handleSetCustomerMode = (value: boolean) => {
    setCustomerModeToStorage(value);
    setCustomerMode(value);
  };

  const envString = environment.isProd ? "prod" : ("stage" as Environment);
  if (!locale) {
    // eslint-disable-next-line no-console
    console.error("Could not find country or language in URL");
  }

  const { data: settings, isLoading } = useSWR<Settings>(
    `${country}/${language}/merged_settings.json`,
    createSettingsFetcher(country, language, "merged_settings.json")
  );

  const combinedSettings = useMemo(() => {
    return {
      ...settings,
      market: country,
      language,
      timezone: currentTimezone,
      environment: envString,
      isLoadingSettings: isLoading,
      customerMode,
      setCustomerMode: handleSetCustomerMode,
    };
  }, [
    settings,
    envString,
    country,
    language,
    isLoading,
    customerMode,
    currentTimezone,
  ]);

  return (
    <SettingsContext.Provider value={combinedSettings}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
